package com.essntl.features.service.ui.addextraservice

import androidx.compose.foundation.layout.*
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusManager
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.button.outline.EsMediumOutlineButton
import com.essntl.uikit.components.button.outline.EsSmallOutlineButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.dropdown.EsInputDropdownMenu
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsMediumIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.input.money.EsMoneyInput
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.delay

@Composable
fun AddExtraServiceScreen(
    state: AddExtraServiceState,
    onEvent: (AddExtraServiceEvent) -> Unit,
    validation: AddExtraServiceFormValidation,
) {
    var serviceSearchText by rememberSaveable { mutableStateOf("") }

    var focusManager by remember { mutableStateOf<FocusManager?>(null) }

    LaunchedEffect(serviceSearchText) {
        delay(500)
        onEvent(
            AddExtraServiceEvent.EditFilterOptions(
                state.filterOptions.copy(
                    searchText = serviceSearchText,
                ),
            ),
        )
    }

    LaunchedEffect(state.addService.isSuccess) {
        if (state.addService.isSuccess)
            state.addService.data?.let { s ->
                focusManager?.clearFocus()
                validation.name.validate(s.title)
                onEvent(AddExtraServiceEvent.UpdateService(service = s))
                onEvent(
                    AddExtraServiceEvent.UpdateExtraServiceFields(
                        state.extraService.value.copy(service = s),
                    ),
                )
                serviceSearchText = ""
            }
    }

    Column(
        verticalArrangement = Arrangement.spacedBy(1.spacing),
        modifier = Modifier.padding(2.spacing),
    ) {
        EsInputDropdownMenu(
            items = state.getAllExtraCategoryService.data,
            selectedItem = state.service,
            text = { it.title },
            onItemSelected = {
                validation.name.validate(it.title)
                onEvent(
                    AddExtraServiceEvent.UpdateService(service = it),
                )
                onEvent(
                    AddExtraServiceEvent.UpdateExtraServiceFields(
                        state.extraService.value.copy(service = it),
                    ),
                )
            },
            label = "Name",
            placeholder = "Enter Name",
            message = "This field is required",
            state = validation.name.state,
            loading = state.getAllExtraCategoryService.isLoading &&
                state.getAllExtraCategoryService.data.isEmpty(),
            enabled = !state.addExtraService.isLoading,
            topContent = {
                Spacer(Modifier.height(0.5.spacing))
                EsInput(
                    value = serviceSearchText,
                    onValueChange = {
                        serviceSearchText = it
                    },
                    placeholder = "Search",
                    prefix = {
                        EsMediumIcon(res = EsIcons.Search)
                    },
                    modifier = Modifier.fillMaxWidth(),
                )

                Spacer(Modifier.height(1.spacing))
            },
            emptyContent = { fm ->
                focusManager = fm

                Row(
                    horizontalArrangement = Arrangement.spacedBy(1.spacing),
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier
                        .height(50.dp)
                        .padding(1.spacing),
                ) {
                    Text(
                        text = "No Service Found!",
                        style = MaterialTheme.esTypography.p2Regular14,
                    )

                    EsSmallOutlineButton(
                        text = "Add Service",
                        loading = state.addService.isLoading,
                        enabled = !state.addService.isLoading,
                        onClick = {
                            onEvent(
                                AddExtraServiceEvent.AddService(
                                    title = serviceSearchText,
                                ),
                            )
                        },
                    )
                }
            },
        ) {
            if (!state.getAllExtraCategoryService.isLastPage) {
                Spacer(modifier = Modifier.height(2.spacing))

                EsMediumOutlineButton(
                    onClick = {
                        onEvent(AddExtraServiceEvent.LoadExtraCategoryServices)
                    },
                    text = "Load more",
                    loading = state.getAllExtraCategoryService.isLoading,
                    modifier =
                    Modifier
                        .align(Alignment.CenterHorizontally),
                )
            }
        }

        EsInput(
            value = state.extraService.value.description ?: "",
            onValueChange = {
                validation.description.validate(it)
                onEvent(
                    AddExtraServiceEvent.UpdateExtraServiceFields(
                        state.extraService.value.copy(description = it),
                    ),
                )
            },
            label = "Description",
            placeholder = "Enter description",
            state = validation.description.state,
            message = validation.description.errorMessage,
        )

        EsMoneyInput(
            value = (state.extraService.value.unitPrice ?: 0) / 100,
            onValueChange = {
                val price = it * 100
                validation.price.validate(price)
                onEvent(
                    AddExtraServiceEvent.UpdateExtraServiceFields(
                        state.extraService.value.copy(unitPrice = price),
                    ),
                )
            },
            label = "Price",
            state = validation.price.state,
            message = validation.price.errorMessage,
        )

        EsInput(
            value = (state.extraService.value.quantity ?: 1).toString(),
            onValueChange = {
                val intValue = it.toIntOrNull() ?: return@EsInput
                validation.amount.validate(intValue)
                onEvent(
                    AddExtraServiceEvent.UpdateExtraServiceFields(
                        state.extraService.value.copy(quantity = intValue),
                    ),
                )
            },
            label = "Amount",
            placeholder = "Enter amount",
            state = validation.amount.state,
            message = validation.amount.errorMessage,
        )

        EsLargePrimaryButton(
            text = "Add",
            onClick = {
                if (validation.validate())
                    onEvent(AddExtraServiceEvent.AddExtraService)
            },
            loading = state.addExtraService.isLoading,
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 1.spacing),
        )
    }
}